/*
 * VPDB - Virtual Pinball Database
 * Copyright (C) 2019 freezy <freezy@vpdb.io>
 *
 * This program is free software; you can redistribute it and/or
 * modify it under the terms of the GNU General Public License
 * as published by the Free Software Foundation; either version 2
 * of the License, or (at your option) any later version.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU General Public License for more details.
 *
 * You should have received a copy of the GNU General Public License
 * along with this program; if not, write to the Free Software
 * Foundation, Inc., 51 Franklin Street, Fifth Floor, Boston, MA  02110-1301, USA.
 * Modified for sigmashake-pinball by SigmaShake, 2026.
 */

import type { MaterialSpec, PhysicsSpec, TableInfoSpec } from "../../shared/contracts";

/**
 * The physics part of a material.
 *
 * Upstream the class also holds the render values. The renderer reads those
 * from the `MaterialSpec`, so the engine keeps only what physics needs.
 */
export class Material {
  public name: string;
  public opacity: number;
  public isOpacityActive: boolean;
  public elasticity: number;
  public elasticityFalloff: number;
  public friction: number;
  /** Degrees. */
  public scatterAngle: number;

  constructor(name: string) {
    this.name = name;
    this.opacity = 1;
    this.isOpacityActive = false;
    this.elasticity = 0;
    this.elasticityFalloff = 0;
    this.friction = 0;
    this.scatterAngle = 0;
  }

  public static fromSpec(spec: MaterialSpec): Material {
    const material = new Material(spec.name);
    material.opacity = spec.opacity ?? 1;
    material.isOpacityActive = spec.isOpacityActive ?? false;
    material.elasticity = spec.elasticity ?? 0;
    material.elasticityFalloff = spec.elasticityFalloff ?? 0;
    material.friction = spec.friction ?? 0;
    material.scatterAngle = spec.scatterAngle ?? 0;
    return material;
  }
}

/** Physics values after the fallback chain: item, then material, then table. */
export interface ResolvedPhysics {
  elasticity: number;
  elasticityFalloff: number;
  friction: number;
  /** Degrees. */
  scatter: number;
  threshold: number | undefined;
}

/**
 * Picks the physics values of an item.
 *
 * Rule: a value on the item wins. If the item has none, the value of its
 * material wins. If the material has none, the table value is used.
 */
export function resolvePhysics(
  spec: PhysicsSpec & { readonly szMaterial?: string },
  info: TableInfoSpec,
  materials?: ReadonlyMap<string, MaterialSpec>,
): ResolvedPhysics {
  const mat = spec.szMaterial !== undefined ? materials?.get(spec.szMaterial) : undefined;
  return {
    elasticity: spec.elasticity ?? mat?.elasticity ?? info.elasticity,
    elasticityFalloff: spec.elasticityFalloff ?? mat?.elasticityFalloff ?? info.elasticityFalloff,
    friction: spec.friction ?? mat?.friction ?? info.friction,
    scatter: spec.scatter ?? mat?.scatterAngle ?? info.scatter,
    threshold: spec.threshold,
  };
}
